import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDownToLine } from "lucide-react";
import {
  detectPlatform,
  getNativeAppVersion,
  compareVersions,
} from "@/lib/appVersion";

interface AppConfig {
  forceUpdateEnabled?: boolean;
  minIosVersion?: string;
  minAndroidVersion?: string;
  iosStoreUrl?: string;
  androidStoreUrl?: string;
}

/**
 * Aggiornamento forzato: se il guscio nativo (App Store / Play Store) è sotto la
 * versione minima remota, mostra un modal bloccante con "Vai allo Store".
 *
 * Sicurezza: FAIL-OPEN. In ogni caso incerto (config non raggiungibile, gate
 * disattivato, piattaforma web, versione nativa non leggibile) NON blocca:
 * meglio non forzare che chiudere fuori gli utenti per errore.
 *
 * La config sta in /app-config.json (pubblica, letta anche prima del login).
 */
export default function ForceUpdateGate() {
  const [storeUrl, setStoreUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const platform = detectPlatform();
        if (platform === "web") return; // browser: niente gate

        const res = await fetch(`/app-config.json?t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const cfg: AppConfig = await res.json();
        if (!cfg.forceUpdateEnabled) return;

        const minVersion =
          platform === "ios" ? cfg.minIosVersion : cfg.minAndroidVersion;
        const url =
          platform === "ios" ? cfg.iosStoreUrl : cfg.androidStoreUrl;
        if (!minVersion || !url) return;

        const native = await getNativeAppVersion();
        if (!native) return; // versione non leggibile → fail-open, non bloccare

        if (compareVersions(native, minVersion) < 0 && !cancelled) {
          setStoreUrl(url);
        }
      } catch {
        // Qualsiasi errore → fail-open.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!storeUrl) return null;

  const goToStore = () => {
    try {
      window.open(storeUrl, "_blank");
    } catch {
      window.location.href = storeUrl;
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-[#1C140C]/30 px-7">
      <div className="w-full max-w-[360px] rounded-[26px] bg-white p-7 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)]">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#86694B]/[0.12]">
          <ArrowDownToLine className="h-8 w-8 text-[#86694B]" />
        </div>
        <h2 className="mt-5 text-center text-[22px] font-bold text-[#111]">
          Aggiornamento richiesto
        </h2>
        <p className="mt-3 text-center text-[15px] leading-relaxed text-[#6A6661]">
          È disponibile una nuova versione di Centrolegno Arreda. Aggiorna l'app per
          continuare a usarla.
        </p>
        <button
          type="button"
          onClick={goToStore}
          className="mt-7 w-full rounded-full bg-[#86694B] py-4 text-[16px] font-semibold text-white active:scale-[0.98] transition-transform"
        >
          Vai allo Store
        </button>
      </div>
    </div>,
    document.body
  );
}
